import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableRow,
    TableCell,
    TableBody,
    TableHead,
    IconButton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PAGE_SIZE = 5;

function Boldertrips() {
    const [trips, setTrips] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [openDialog, setOpenDialog] = useState(false);

    // Form State
    const [form, setForm] = useState({
        date: "",
        vehicleId: "",
        vehicleNo: "",
        vehicleVolume: "", // Auto-filled but now fully editable
        noOfTrips: "",
        totalBrass: ""
    });

    // ================= Load Initial Data =================
    useEffect(() => {
        loadTrips();
        loadVehicles();
    }, []);

    const loadTrips = async () => {
        try {
            const snap = await getDocs(collection(db, "boldertrips"));
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setTrips(arr);
        } catch (error) {
            console.error("Error fetching trips:", error);
        }
    };

    const loadVehicles = async () => {
        try {
            const snap = await getDocs(collection(db, "prodvehicles"));
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setVehicles(arr);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        }
    };

    // ================= Form Change Handlers =================
    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedForm = { ...form, [name]: value };

        // If either No of Trips OR Vehicle Volume changes, recalculate Total Brass
        if (name === "noOfTrips" || name === "vehicleVolume") {
            const tripsNum = parseFloat(name === "noOfTrips" ? value : form.noOfTrips) || 0;
            const volNum = parseFloat(name === "vehicleVolume" ? value : form.vehicleVolume) || 0;
            updatedForm.totalBrass = (tripsNum * volNum).toFixed(2);
        }

        setForm(updatedForm);
    };

    // Auto-fetch Vehicle metrics but allow modifications
    const handleVehicleChange = (e) => {
        const selectedId = e.target.value;
        const selectedVehicle = vehicles.find((v) => v.id === selectedId);

        if (selectedVehicle) {
            const volNum = parseFloat(selectedVehicle.volume) || 0;
            const tripsNum = parseFloat(form.noOfTrips) || 0;

            setForm({
                ...form,
                vehicleId: selectedId,
                vehicleNo: selectedVehicle.vehicleNo || "",
                vehicleVolume: selectedVehicle.volume || "", // Initial autofetch setup
                totalBrass: (tripsNum * volNum).toFixed(2)
            });
        } else {
            setForm({ ...form, vehicleId: "", vehicleNo: "", vehicleVolume: "", totalBrass: "" });
        }
    };

    // ================= Reset & Context Closes =================
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingId(null);
        setForm({
            date: "",
            vehicleId: "",
            vehicleNo: "",
            vehicleVolume: "",
            noOfTrips: "",
            totalBrass: ""
        });
    };

    // ================= Save / Update Data CRUD =================
    const saveTrip = async () => {
        const { date, vehicleId, vehicleVolume, noOfTrips } = form;

        if (!date || !vehicleId || !vehicleVolume || !noOfTrips) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        try {
            const id = editingId || doc(collection(db, "boldertrips")).id;
            await setDoc(doc(db, "boldertrips", id), {
                ...form,
                createdTime: editingId ? (form.createdTime || Timestamp.now()) : Timestamp.now(),
            });

            Swal.fire({
                title: "Success",
                text: editingId ? "Bolder trip updated successfully" : "Bolder trip added successfully",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            handleCloseDialog();
            loadTrips();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    // ================= Edit Trigger Setup =================
    const editTrip = (trip) => {
        setForm({
            date: trip.date || "",
            vehicleId: trip.vehicleId || "",
            vehicleNo: trip.vehicleNo || "",
            vehicleVolume: trip.vehicleVolume || "",
            noOfTrips: trip.noOfTrips || "",
            totalBrass: trip.totalBrass || "",
            ...(trip.createdTime && { createdTime: trip.createdTime })
        });
        setEditingId(trip.id);
        setOpenDialog(true);
    };

    // ================= Delete Record Action =================
    const deleteTrip = async (id) => {
        const res = await Swal.fire({
            title: "Delete Trip Entry?",
            text: "This removal operation cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (!res.isConfirmed) return;

        try {
            await deleteDoc(doc(db, "boldertrips", id));
            Swal.fire({
                title: "Deleted",
                text: "Trip entry successfully cleared.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            loadTrips();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    const filtered = trips.filter(
        (t) =>
            t.date?.includes(search) ||
            t.vehicleNo?.toLowerCase().includes(search.toLowerCase())
    );
    const sorted = [...filtered].sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
    });

    // Paginate the sorted array
    const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box minHeight="100vh" bgcolor="#f4f6f8">
            <Box flex={1} p={3}>

                {/* Header Toolbar */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={800}>
                        Bolder Trips Logs
                    </Typography>
                    <Button variant="contained" onClick={() => setOpenDialog(true)}>
                        Add New Trip
                    </Button>
                </Box>

                {/* Search Box */}
                <TextField
                    label="Search entries by Vehicle Number or Date"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    sx={{ mb: 3, width: "100%", bgcolor: "#fff" }}
                    size="small"
                />

                {/* Logs Table */}
                <Card elevation={2}>
                    <CardContent>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr No</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Vehicle No</TableCell>
                                    <TableCell>Vehicle Vol</TableCell>
                                    <TableCell>No of Trips</TableCell>
                                    <TableCell>Total Brass</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.length > 0 ? (
                                    paginated.map((trip, index) => (
                                        <TableRow key={trip.id} hover>
                                            <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                            <TableCell>{trip.date}</TableCell>
                                            <TableCell>{trip.vehicleNo}</TableCell>
                                            <TableCell>{trip.vehicleVolume}</TableCell>
                                            <TableCell>{trip.noOfTrips}</TableCell>
                                            <TableCell style={{ fontWeight: 'bold', color: '#1b5e20' }}>
                                                {trip.totalBrass}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton color="primary" onClick={() => editTrip(trip)}>
                                                    <EditOutlined />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => deleteTrip(trip.id)}>
                                                    <DeleteOutline />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" style={{ padding: "20px" }}>
                                            No tracking trips log history mapped.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Pagination */}
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={Math.ceil(filtered.length / PAGE_SIZE)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                color="primary"
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Dialog Form Modal */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>{editingId ? "Edit Trip Logs Info" : "Log New Bolder Trip"}</DialogTitle>
                    <DialogContent>
                        <Box mt={1} display="flex" flexDirection="column" gap={2.5}>

                            <TextField
                                label="Select Date"
                                type="date"
                                name="date"
                                value={form.date}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />

                            <FormControl fullWidth size="small" required>
                                <InputLabel id="vehicle-select-label">Select Vehicle</InputLabel>
                                <Select
                                    labelId="vehicle-select-label"
                                    label="Select Vehicle"
                                    value={form.vehicleId}
                                    onChange={handleVehicleChange}
                                >
                                    <MenuItem value=""><em>-- Select Available Vehicle --</em></MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.vehicleNo} ({v.vehicleType})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* NOW EDITABLE: Automatically fetches volume on dropdown change, but accepts user modifications */}
                            <TextField
                                label="Vehicle Volume"
                                type="number"
                                name="vehicleVolume"
                                value={form.vehicleVolume}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                inputProps={{ min: "0", step: "0.01" }}
                            />

                            <TextField
                                label="Number of Trips"
                                type="number"
                                name="noOfTrips"
                                value={form.noOfTrips}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                inputProps={{ min: "1", step: "1" }}
                            />

                            <TextField
                                label="Total Volume of Bolder (in Brass)"
                                value={form.totalBrass}
                                fullWidth
                                disabled
                                size="small"
                                variant="filled"
                                slotProps={{
                                    input: { style: { fontWeight: 'bold', color: '#1b5e20' } }
                                }}
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                        <Button variant="contained" onClick={saveTrip}>
                            {editingId ? "Update Log" : "Add Log"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </Box>
    );
}

export default Boldertrips;